import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const FITS = ["Classic", "Oversized", "Mesh"];
const SIZES = ["S", "M", "L", "XL", "XXL"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fit = params.get("fit") || "";
  const size = params.get("size") || "";
  const collection = params.get("collection") || "";
  const bestSeller = params.get("best_seller") === "true";

  useEffect(() => {
    api.get("/products/collections").then((r) => setCollections(r.data.collections || []));
  }, []);

  // Oversized fit already scopes to the oversized tees, so the redundant
  // Collection filter is hidden and any stale collection param is ignored.
  const visibleCollections = fit === "Oversized" ? [] : collections;
  const effectiveCollection = fit === "Oversized" ? "" : collection;

  useEffect(() => {
    if (fit === "Oversized" && collection) {
      const next = new URLSearchParams(params);
      next.delete("collection");
      setParams(next, { replace: true });
    }
  }, [fit, collection]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (fit) q.fit = fit;
    if (size) q.size = size;
    if (effectiveCollection) q.collection = effectiveCollection;
    if (bestSeller) q.best_seller = true;
    api.get("/products", { params: q }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [fit, size, effectiveCollection, bestSeller]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = [fit, size, effectiveCollection, bestSeller ? "1" : ""].filter(Boolean).length;

  const FilterGroup = ({ label, options, current, keyName }) => (
    <div className="mb-6">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`filter-${keyName}-${o}`}
            onClick={() => setFilter(keyName, current === o ? "" : o)}
            className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider border transition-colors ${
              current === o
                ? "bg-jamaica-gold text-black border-jamaica-gold"
                : "border-border text-zinc-300 hover:border-jamaica-gold"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-jamaica-green mb-2">Di Whole Rack</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight">SHOP ALL</h1>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold uppercase text-sm">Filters</h2>
            {activeCount > 0 && (
              <button data-testid="clear-filters" onClick={clearAll} className="font-mono text-[10px] uppercase text-jamaica-gold">
                Clear ({activeCount})
              </button>
            )}
          </div>
          <FilterGroup label="Fit" options={FITS} current={fit} keyName="fit" />
          <FilterGroup label="Size" options={SIZES} current={size} keyName="size" />
          {visibleCollections.length > 0 && (
            <FilterGroup label="Collection" options={visibleCollections} current={collection} keyName="collection" />
          )}
        </aside>

        <div>
          <p className="font-mono text-xs text-zinc-500 mb-4" data-testid="result-count">
            {loading ? "Loading…" : `${products.length} item${products.length === 1 ? "" : "s"}`}
          </p>
          {!loading && products.length === 0 && (
            <div className="py-24 text-center text-zinc-500 font-mono text-sm uppercase tracking-widest">
              Nutten deh yah. Try another filter.
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
